import { Classes, Colors, Dialog, Intent, Checkbox, ButtonGroup } from "@blueprintjs/core";
import { ButtonContainer, FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link, useHistory } from "react-router-dom";

import { CountryApi, TitlesApi, TitleSearch, TitlesGridDto } from "../../../../../api";
import { ERoutes } from "../../../../../ERoutes";
import {
  AddButton,
  CustomDataTable,
  EditButton,
  ViewButton,
  DeleteButton,
  ExportButton,
} from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { useApiService, useTl, useDeleteApi } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { CountryTitlePanel } from "./CountryTitlePanel";

export interface ITitleListPageProps {
  countryId: number;
}

export const TitleListPage: React.FunctionComponent<ITitleListPageProps> = ({ countryId }) => {
  const { t } = useTl();
  const api = useApiService(TitlesApi);
  const apiCountry = useApiService(CountryApi);
  const { currentLanguage } = useGlobalData();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [currentTitleId, setCurrentTitleId] = React.useState(0);
  const [sobjState, setsobjState] = React.useState<any>();

  const tableState = useGridState({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
    sortKeys: { name: "ASC" },
  });
  const hitory = useHistory();
  const { totalCount } = tableState;
  const searchFunction = React.useCallback(
    (sObj: TitleSearch) => {
      setsobjState(TitleSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId, countryId: countryId }));
      return api.baseSearch(
        TitleSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId, countryId: countryId })
      );
    },
    [api, countryId, currentLanguage.languageId]
  );

  const { search, loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });
  const [init, setInit] = React.useState<boolean>(false);
  const [belgian, setBelgian] = React.useState<boolean>(true);

  const addItemFunction = React.useCallback(
    (id: number, editMode: "view" | "edit") => {
      hitory.push(`${ERoutes.country}/${countryId}/titles/${editMode}`);
      setCurrentTitleId(id);
      setDialogOpened(true);
    },
    [countryId, hitory]
  );

  const { deleteItem: deleteTitle } = useDeleteApi<number>(
    React.useMemo(
      () => ({
        deleteApiFn: (data) => api.delete(data),
        onDeletedFn: () => search(),
      }),
      [api, search]
    )
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: TitlesGridDto) => (
          <ButtonContainer>
            <ViewButton minimal={true} onClick={() => addItemFunction(row.titleId, "view")} />
            <EditButton minimal={true} onClick={() => addItemFunction(row.titleId, "edit")} />
            {!belgian && <DeleteButton minimal={true} onDelete={() => deleteTitle(row.titleId)} />}
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Name),
        fieldName: "name",
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "type",
        render: (row: TitlesGridDto) => (
          <Link target="_blank" to={`${ERoutes.titleType}/${row.titleTypeId}/detail/view`}>
            {row.type}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Abreviation),
        fieldName: "abreviation",
      },
      {
        header: () => t(ETLCodes.WorkRequired),
        fieldName: "workRequired",
        render: (row: TitlesGridDto) => <Checkbox checked={row.workRequired} disabled />,
      },
      belgian && {
        header: () => t(ETLCodes.CanBeUsed),
        fieldName: "canBeUsed",
        render: (row: TitlesGridDto) => <Checkbox checked={row.canBeUsed} disabled />,
      },
    ],
    [addItemFunction, belgian, deleteTitle, t]
  );

  React.useEffect(() => {
    search();
    if (!init) {
      setInit(true);

      apiCountry.isBelgian(countryId).then((bool) => {
        setBelgian(bool.value);
      });
    }
  }, [apiCountry, countryId, currentLanguage, init, belgian, search]);
  return (
    <>
      <FieldSet
        title={t(ETLCodes.Titles, { count: totalCount })}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {!belgian && (
              <AddButton
                onClick={(e) => {
                  e.stopPropagation();
                  addItemFunction(0, "edit");
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>

      <Dialog
        title={t(ETLCodes.Titles)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1000, height: 320, background: Colors.WHITE }}
        className={Classes.DIALOG}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 250 }}>
          <CountryTitlePanel
            search={search}
            countryId={countryId}
            currentTitleId={currentTitleId}
            belgian={belgian}
            onDelete={() => {
              setDialogOpened(false);
            }}
          />
        </div>
      </Dialog>
    </>
  );
};
